import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getEligibleCandidates } from "../../../models/RequirementsModel";
import { getData as getCandidateStatus } from "../../../models/CandidateStatusModel";
import { submitToRequirement } from "../../../models/CandidatesModel";
import { apiError } from "../../../helpers/ErrorHelper";
import { checkPermission } from "../../../helpers/GeneralHelper";
import {
  formatEligibilityBreakdown,
  getCandidateDisplayName,
  getEligibilityTone,
} from "../../../helpers/RequirementEligibilityHelper";
import TableLoader from "../../skeletons/TableLoader";
import SubmitCandidateToRequirementModal from "../../modals/SubmitCandidateToRequirementModal";
import { InterviewAddFromCandidate } from "../../modals/InterviewAddFromCandidate";

export default function RequirementEligibleCandidates({
  requirement,
  userToken,
  highlighted = false,
  onAddCandidate,
}) {
  const navigate = useNavigate();
  const requirementId = requirement?._requirement_id;
  const canSubmit = checkPermission("candidates", "edit");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({});
  const [statuses, setStatuses] = useState([]);
  const [submitModal, setSubmitModal] = useState(null);
  const [scheduleModal, setScheduleModal] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadMatches = useCallback(async () => {
    if (!userToken || !requirementId) {
      setRows([]);
      setMeta({});
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await getEligibleCandidates(userToken, requirementId, {
        limit: 25,
        min_percent: 15,
      });
      setRows(Array.isArray(result?.candidates) ? result.candidates : []);
      setMeta(result?.meta || {});
    } catch (error) {
      console.error(error);
      toast.error("Could not load eligible candidates.");
      setRows([]);
      setMeta({});
    }
    setLoading(false);
  }, [userToken, requirementId]);

  useEffect(() => {
    loadMatches();
  }, [loadMatches]);

  useEffect(() => {
    if (!userToken) {
      return;
    }

    const loadStatuses = async () => {
      try {
        const response = await getCandidateStatus({ user_token: userToken, page: 1, limit: -1 });
        setStatuses(response?.candidate_status || []);
      } catch (error) {
        console.error(error);
      }
    };

    loadStatuses();
  }, [userToken]);

  const openCandidate = (row) => {
    navigate(`/candidates/${row.candidate_unique_id}/view`, {
      state: {
        data: row,
        module: {
          title: "Candidates",
          singularTitle: "Candidate",
          key: "candidates",
          route: "candidates",
          primaryCol: "_candidate_id",
        },
      },
    });
  };

  const openSubmitModal = (row) => {
    if (!canSubmit) {
      toast.error("You do not have permission to submit candidates.");
      return;
    }
    setSubmitModal({ candidate: row });
  };

  const runSubmit = async ({ status, remark }) => {
    if (!submitModal?.candidate || !status) {
      return false;
    }

    const candidate = submitModal.candidate;
    setSaving(true);
    try {
      const response = await submitToRequirement(
        userToken,
        candidate._candidate_id,
        requirementId,
        {
          candidate_status_id: status._status_id,
          candidate_comments: remark || "",
        }
      );

      if (response?.status) {
        toast.success(response.message || "Candidate submitted successfully.");
        setSubmitModal(null);
        await loadMatches();

        if (Number(status.status_schedule_interview) === 1) {
          setScheduleModal({
            candidate: {
              ...candidate,
              candidate_client_id: requirement?.requirement_client_id,
              candidate_requirement_id: requirement?._requirement_id,
              client_name: requirement?.client_name,
              requirement_name: requirement?.requirement_name,
              candidate_status_id: status._status_id,
            },
            statusId: Number(status._status_id),
          });
        }
        return true;
      }

      if (response?.already_submitted) {
        toast.info(response.message);
        setSubmitModal(null);
        await loadMatches();
      } else {
        toast.error(response?.message || "Could not submit candidate.");
      }
      return false;
    } catch (error) {
      apiError(error);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitConfirm = async ({ status, remark }) => {
    await runSubmit({ status, remark });
  };

  return (
    <div
      className={`requirement-eligible-panel${highlighted ? " is-highlighted" : ""}`}
      id="requirement-eligible-candidates"
    >
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <h6 className="mb-1 fw-semibold text-primary">Suggested candidates</h6>
          <p className="text-muted small mb-0">
            Ranked mainly by <strong>primary skills</strong> (75% weight), then secondary skills,
            experience, and location. Candidates already submitted to this job are excluded.
          </p>
          {meta?.primary_skill_names?.length > 0 ? (
            <p className="small mb-0 mt-1">
              <span className="text-muted">Required primary skills: </span>
              {meta.primary_skill_names.join(", ")}
            </p>
          ) : null}
        </div>
        <button type="button" className="btn btn-sm btn-light" onClick={loadMatches}>
          <i className="ri-refresh-line me-1" />
          Refresh
        </button>
      </div>

      {loading ? (
        <TableLoader noOfColumn={4} />
      ) : rows.length === 0 ? (
        <div className="requirement-eligible-empty">
          <p className="mb-2 text-muted">
            {meta?.message ||
              "No candidates scored at least 15% match. Ensure candidates have the same primary skills as this requirement (skill master IDs must match)."}
          </p>
          {meta?.has_primary_skills && onAddCandidate ? (
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={onAddCandidate}>
              Add a new candidate
            </button>
          ) : null}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle requirement-eligible-table mb-0">
            <thead>
              <tr>
                <th>Candidate</th>
                <th style={{ minWidth: 140 }}>Eligibility</th>
                <th>Skills match</th>
                <th>Experience</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const tone = getEligibilityTone(row.eligibility_percent);
                const breakdownLines = formatEligibilityBreakdown(row.breakdown);

                return (
                  <tr key={row._candidate_id}>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 text-start fw-semibold"
                        onClick={() => openCandidate(row)}
                      >
                        {getCandidateDisplayName(row)}
                      </button>
                      {row.candidate_mobile ? (
                        <div className="small text-muted">{row.candidate_mobile}</div>
                      ) : null}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span className={`requirement-eligible-badge requirement-eligible-badge--${tone}`}>
                          {row.eligibility_percent}%
                        </span>
                        <div className="progress requirement-eligible-progress flex-grow-1">
                          <div
                            className={`progress-bar requirement-eligible-bar--${tone}`}
                            style={{ width: `${row.eligibility_percent}%` }}
                          />
                        </div>
                      </div>
                      {breakdownLines.length > 0 ? (
                        <ul className="small text-muted mb-0 mt-1 ps-3">
                          {breakdownLines.map((line) => (
                            <li key={line}>{line}</li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                    <td>
                      {row.primary_skills_total > 0 ? (
                        <div className="small fw-semibold mb-1">
                          Primary: {row.primary_skills_matched ?? 0}/{row.primary_skills_total}
                        </div>
                      ) : null}
                      {(row.matched_skill_names || []).length > 0 ? (
                        <div className="small">
                          <span className="text-success">Matched: </span>
                          {(row.matched_skill_names || []).join(", ")}
                        </div>
                      ) : (
                        <span className="small text-muted">No matching skills</span>
                      )}
                      {(row.missing_skill_names || []).length > 0 ? (
                        <div className="small text-muted">
                          Missing: {(row.missing_skill_names || []).slice(0, 4).join(", ")}
                          {(row.missing_skill_names || []).length > 4 ? "…" : ""}
                        </div>
                      ) : null}
                    </td>
                    <td className="small">
                      <div>Total: {row.candidate_experience_total || "—"}</div>
                      <div>Relevant: {row.candidate_experience_relevant || "—"}</div>
                      <div className="text-muted">
                        {row.candidate_present_location || row.candidate_preffered_location || "—"}
                      </div>
                    </td>
                    <td className="text-end">
                      <div className="d-flex flex-column gap-1 align-items-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openCandidate(row)}
                        >
                          View
                        </button>
                        {canSubmit ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => openSubmitModal(row)}
                            disabled={saving}
                          >
                            Submit to job
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-muted small mt-3 mb-0">
        Score uses primary/secondary skills, total and relevant experience vs this requirement, and
        location when set. Add skills and experience on the requirement details to improve matches.
      </p>

      <SubmitCandidateToRequirementModal
        show={Boolean(submitModal)}
        onHide={() => setSubmitModal(null)}
        candidate={submitModal?.candidate}
        requirement={requirement}
        statuses={statuses}
        saving={saving}
        onConfirm={handleSubmitConfirm}
      />

      {scheduleModal && (
        <InterviewAddFromCandidate
          showModal={Boolean(scheduleModal)}
          setShowModal={(open) => {
            if (!open) setScheduleModal(null);
          }}
          candidateData={scheduleModal.candidate}
          statusId={scheduleModal.statusId}
          onDismiss={() => {
            toast.info("Candidate was submitted. Interview was not scheduled.");
          }}
        />
      )}
    </div>
  );
}
